import { useState } from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { Sparkle, Heart } from "./Decorations";

function encode(data) {
  return Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");
}

export default function Contact() {
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [values, setValues] = useState({ name: "", email: "", subject: "", message: "" });

  const onChange = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      // Netlify Forms, submit via fetch so user stays on page; falls back to standard
      // POST if JS is disabled (form action="/thank-you").
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...values }),
      });
      if (!res.ok) throw new Error(`Form submission failed: ${res.status}`);
      setStatus("sent");
      setValues({ name: "", email: "", subject: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 bg-lavender-bg overflow-hidden"
    >
      <span
        className="float-shape animate-float"
        style={{ width: 240, height: 240, background: "#C7A5D9", top: 20, left: -60 }}
      />
      <Sparkle className="absolute top-20 right-20" color="#8E44AD" size={20} />
      <Heart className="absolute bottom-20 right-12" color="#C7A5D9" size={26} />
      <div className="relative max-w-5xl mx-auto px-5 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <p className="pill mb-5 reveal">Get in Touch</p>
          <h2
            className="h-display text-3xl sm:text-4xl title-underline reveal"
            data-testid="contact-title"
          >
            Connect with Dr. Maggie Cadet
          </h2>
          <p className="mt-7 text-charcoal/80 reveal">
            For speaking opportunities, collaborations, media inquiries, educational content, and
            general messages, please get in touch.
          </p>
        </div>

        <div
          data-testid="contact-card"
          className="mt-12 mx-auto max-w-2xl bg-white rounded-3xl p-8 sm:p-10 shadow-[0_30px_70px_-30px_rgba(75,31,99,0.4)] border border-lavender/60 transition-transform duration-500 hover:-translate-y-1 reveal"
        >
          {status === "sent" ? (
            <div
              data-testid="contact-success"
              className="text-center py-8"
            >
              <div className="inline-flex h-14 w-14 rounded-full bg-lavender-bg text-royal items-center justify-center mb-5">
                <CheckCircle2 size={26} />
              </div>
              <h3 className="font-display text-xl tracking-[0.18em] uppercase text-plum">
                Thank You
              </h3>
              <p className="mt-3 text-charcoal/75">
                Your message has been received. Dr. Maggie will be in touch soon.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="mt-7 btn-outline-purple text-sm"
                data-testid="contact-send-another"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              name="contact"
              method="POST"
              action="/thank-you"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={onSubmit}
              className="grid gap-5"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="sr-only" aria-hidden="true">
                <label>
                  Don't fill this out if you're human:
                  <input name="bot-field" tabIndex="-1" autoComplete="off" />
                </label>
              </p>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="field-label">Name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={values.name}
                    onChange={onChange}
                    className="field"
                    data-testid="contact-name"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="field-label">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={values.email}
                    onChange={onChange}
                    className="field"
                    data-testid="contact-email"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="field-label">Subject</label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={values.subject}
                  onChange={onChange}
                  className="field"
                  data-testid="contact-subject"
                />
              </div>

              <div>
                <label htmlFor="message" className="field-label">Message</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={values.message}
                  onChange={onChange}
                  className="field resize-none"
                  data-testid="contact-message"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={status === "sending"}
                className="btn-purple justify-center disabled:opacity-70 disabled:cursor-not-allowed mt-2"
              >
                {status === "sending" ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Message <Send size={15} />
                  </>
                )}
              </button>

              {status === "error" && (
                <p className="text-sm text-red-600 text-center">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
